import React, { useCallback, useContext, useEffect, useState } from "react";
import styles from "../../form.module.css";
import {
  Button,
  Checkbox,
  Col,
  DatePicker,
  Flex,
  Form,
  Input,
  Radio,
  Row,
  Select,
  Spin,
  Typography,
} from "antd";
import useFetch from "../../../../hooks/useFetch";
import useToast from "../../../../hooks/useToast";
import {
  eAnswerType,
  eFORM_FOR,
  eFORMSTATUS,
  eResultCode,
  eSTAGE,
  eUSER_FORM_TYPE,
  eYES_OR_NO,
  INPUT_LENGTH,
} from "../../../../utils/enum";
import { EXAM_INSTITUTE_LIST } from "../../../../utils/constants";
import { ToastOpen, ToastType } from "../../../../state/toast/slice";
import { CheckCircleOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import Text, { LanguageContext } from "../../../../context/language-context";
import { useNavigate, useSearchParams } from "react-router-dom";
import { UserContext } from "../../../../context";
import {
  GetFormData,
  GetSpecificUserData,
  updateField,
} from "../../../../utils/api.constant";
import { FaCheck } from "react-icons/fa";
import { TFormProps } from "../../../../types";
import { LoaderContext } from "../../../../context/loader";
import EncryptUtils from "../../../../utils/encrypt";
import Utils from "../../../../utils/index";
import { allowOnlyNumericInput_onKeyDown } from "../../../../utils/input.validator";
import { Regex } from "../../../../utils/validation";
import { Table } from "antd";
import type { TableProps } from "antd/es/table";
import {
  getNumberInLanguage,
  stringDateInLanguage,
} from "../../../../utils/helperFunctions";
import dayjs from "dayjs";
import { useFormAutoSave } from "../../../../hooks/useFormAutoSave";
import { DocumentUploadField } from "../../../../components/document-upload";
import FormHelpTooltip from "../../../../components/common/FormHelpTooltip";
import { useVedOptions } from "../../../../hooks/useVedOptions";
import { CURRENT_YEAR } from "../../../../utils/constants";

// Define all Date field keys for this form to enable robust date conversion
const DATE_FIELD_KEYS = [
  "STUDENT_2026_PT1_Q4", // DOB
  "STUDENT_2026_PT1_Q20_B", // Exam Month-Year
  "STUDENT_PT1_Q30", // Affidavit Date
];

const formItemLayout = {
  labelCol: {
    span: 24,
  },
  wrapperCol: {
    span: 24,
  },
};

const StudentPartOneCurrent = ({
  disabled,
  currentFormId,
  currentUserId,
  currentFormFor,
}: TFormProps) => {
  const [form] = Form.useForm();
  const { post } = useFetch();
  const isInstituteOther = Form.useWatch("STUDENT_2026_PT1_Q20_D", form);
  const { onShowToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [formId, setFormId] = useState<string | null>(null);
  const [formFor, setFormFor] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | number | null>(null);
  const { user } = useContext(UserContext);
  const { userLanguage } = useContext(LanguageContext);
  const [documentsData, setDocumentsData] = useState<any>([]);
  const navigate = useNavigate();
  const DROPDOWN_PLACEHOLDER = Text({
    tid: "DROPDOWN_PLACEHOLDER",
    def: "DROPDOWN_PLACEHOLDER",
  });

  // Loader Context For show Loader
  const contextForLoader = useContext(LoaderContext);
  const { isLoading, setIsLoading } = contextForLoader as any;

  const vedMapping = useVedOptions();

  const [samvedShakhaOptions, setSamvedShakhaOptions] = useState<any[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isReviewMode, setIsReviewMode] = useState(false);
  const [sameAsCurrentAddress, setSameAsCurrentAddress] = useState(false);

  // Watch affidavitAccepted to trigger re-renders when checkbox changes
  const affidavitAccepted = Form.useWatch("affidavitAccepted", form);

  useEffect(() => {
    if (searchParams) {
      // Decrypt and set formId and formFor from URL search parameters
      const formId = EncryptUtils.decrypt(searchParams.get("id") || "");
      const formFor = EncryptUtils.decrypt(searchParams.get("ff") || "");
      const uId = EncryptUtils.decrypt(searchParams.get("ui") || "");
      setFormId(formId);
      setFormFor(formFor);
      setUserId(uId);
    }
    if (currentFormId) {
      // Decrypt and set formId from props if available
      setFormId(EncryptUtils.decrypt(currentFormId));
    }
    if (currentUserId) {
      setUserId(EncryptUtils.decrypt(currentUserId));
    }
    if (currentFormFor) {
      setFormFor(EncryptUtils.decrypt(currentFormFor));
    }
  }, [searchParams, currentFormId, currentUserId, currentFormFor]);

  const handleFieldChange = async (
    field: string,
    value: any,
    answerType: number = eAnswerType.TEXT,
    bulkPayload?: any[],
    isSubmitted?: boolean,
  ) => {
    if (formId?.length) {
      try {
        setIsSubmitting(true);

        const answers: any[] = [];

        // Handle single field
        if (field.length > 0) {
          const trimmedValue = typeof value === "string" ? value.trim() : value;
          if (trimmedValue === null || trimmedValue === undefined) {
            form.setFieldValue(field, "");
            // Continue execution to save the empty string to backend
          }

          // Auto-detect answer type from JSON strings
          let detectedType = answerType;
          if (typeof trimmedValue === "string") {
            if (trimmedValue.startsWith("[")) {
              detectedType = eAnswerType.ARRAY;
            } else if (trimmedValue.startsWith("{")) {
              detectedType = eAnswerType.OBJECT;
            }
          }

          // Format answer based on type
          let formattedAnswer = trimmedValue;
          if (
            detectedType === eAnswerType.ARRAY ||
            detectedType === eAnswerType.OBJECT
          ) {
            formattedAnswer =
              typeof trimmedValue === "string"
                ? trimmedValue
                : JSON.stringify(trimmedValue);
          } else if (
            detectedType === eAnswerType.DATE &&
            typeof trimmedValue === "object" &&
            trimmedValue?.format
          ) {
            formattedAnswer = trimmedValue.format("YYYY-MM-DD");
          }

          answers.push({
            questionKey: field,
            answerType: detectedType,
            answer: formattedAnswer,
            part: eSTAGE.STAGE1,
          });
        }

        // Handle bulk payload (multiple answers)
        if (bulkPayload && Array.isArray(bulkPayload)) {
          answers.push(...bulkPayload);
        }

        const payload: any = {
          data: {
            id: formId,
            answers: answers,
          },
        };

        if (isSubmitted) {
          payload.data.isSubmitted = true;
        }

        const response = await post(updateField, payload);
        const { dataResponse } = response;
        const { returnCode, description } = dataResponse;
        if (returnCode == eResultCode.SUCCESS) {
          if (isSubmitted) {
            onShowToast({
              type: ToastType.success,
              title: <FaCheck />,
              position: ToastOpen.leftBottom,
              content: "Form Submitted Successfully!",
            });
            setTimeout(() => {
              navigate("/");
            }, 1000);
          }
        } else if (returnCode == eResultCode.INVALID_REQUEST) {
          onShowToast({
            type: ToastType.error,
            title: <ExclamationCircleOutlined />,
            position: ToastOpen.leftBottom,
            content: description,
          });
        }
      } catch (error) {
        console.error("Student Form 1 Error : ", error);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const getFormData = async () => {
    setIsLoading(true);
    try {
      const payload: any = {
        data: {
          id: formId,
          part: eSTAGE.STAGE1,
          year: CURRENT_YEAR,
          userFormType: eUSER_FORM_TYPE.STUDENT,
          userId: userId,
          formFor: formFor,
        },
      };

      const response = await post(GetFormData, payload);
      const { dataResponse, data } = response;
      const { returnCode } = dataResponse;

      if (returnCode == eResultCode.SUCCESS && Array.isArray(data)) {
        // Convert array of answers to form field values
        const formattedData: { [key: string]: any } = {};
        const docs: any[] = [];

        data.forEach((answer: any) => {
          const { questionKey, answerType, answer: answerValue } = answer;

          if (!questionKey) return;

          // Parse based on answerType
          switch (answerType) {
            case eAnswerType.ARRAY:
            case eAnswerType.OBJECT:
              try {
                formattedData[questionKey] = answerValue
                  ? JSON.parse(answerValue)
                  : answerType === eAnswerType.ARRAY
                    ? []
                    : {};
              } catch (error) {
                console.error(`Error parsing ${questionKey}:`, error);
                formattedData[questionKey] =
                  answerType === eAnswerType.ARRAY ? [] : {};
              }
              break;
            case eAnswerType.FILE:
              // File fields store document ID, don't set in form
              formattedData[questionKey] = answerValue || "";
              if (answerValue) {
                docs.push({
                  docPath: answerValue,
                  labelName: questionKey, // linking doc to question
                  docName: "Document", // Default name
                });
              }
              break;
            case eAnswerType.DATE:
              // Convert date string to dayjs object for DatePicker
              formattedData[questionKey] = answerValue
                ? dayjs(answerValue)
                : null;
              break;
            case eAnswerType.TEXT:
            default:
              formattedData[questionKey] = answerValue || "";

              // Force Date Conversion for known date fields if they come as TEXT
              if (DATE_FIELD_KEYS.includes(questionKey) && answerValue) {
                const parsedDate = dayjs(answerValue);
                formattedData[questionKey] = parsedDate.isValid()
                  ? parsedDate
                  : null;
              }
              break;
          }
        });

        if (docs.length > 0) {
          setDocumentsData(docs);
        }

        if (Object.keys(formattedData).length > 0) {
          form.setFieldsValue(formattedData);

          // Sync affidavitAccepted state with loaded form data
          if (
            formattedData.affidavitAccepted ||
            formattedData.affidavitAccepted === "1"
          ) {
            form.setFieldValue("affidavitAccepted", true);
          }
        }
      }
    } catch (error) {
      console.error("Student 1 Form Error", error);
    } finally {
      setIsLoading(false);
    }
  };

  const getMappedData = (data: any) => {
    if (!data) return {};

    const mappedData: { [key: string]: any } = {
      // Name - Q1
      STUDENT_2026_PT1_Q1: data.STUDENT_PT1_Q1 || "",

      // Father's Name - Q2 (2025 Q5)
      STUDENT_2026_PT1_Q2: data.STUDENT_PT1_Q5 || "",

      // Mother's Name - Q3 (2025 Q6)
      STUDENT_2026_PT1_Q3: data.STUDENT_PT1_Q6 || "",

      // DOB - Q4 (2025 Q3)
      STUDENT_2026_PT1_Q4: data.STUDENT_PT1_Q3
        ? dayjs(data.STUDENT_PT1_Q3).isValid()
          ? dayjs(data.STUDENT_PT1_Q3)
          : null
        : null,

      // Birth Place - Q5 (2025 Q4)
      STUDENT_2026_PT1_Q5: data.STUDENT_PT1_Q4 || "",

      // Rishi-Gotra - Q6 (2025 Q7)
      STUDENT_2026_PT1_Q6: data.STUDENT_PT1_Q7 || "",

      // Aadhaar - Q7 (2025 Q2)
      STUDENT_2026_PT1_Q7: data.STUDENT_PT1_Q2 || "",

      // Address (Correspondence) - Q8 (2025 Q10)
      STUDENT_2026_PT1_Q8: data.STUDENT_PT1_Q10 || "",

      // Address (Permanent) - Q9 (New in 2026, or use Q10 if same?)
      // Leaving empty as 2025 didn't have explicit permanent address field separate from correspondence usually
      STUDENT_2026_PT1_Q9: "",

      // Mobile - Q10 (2025 Q11)
      STUDENT_2026_PT1_Q10: data.STUDENT_PT1_Q11 || "",

      // Email - Q11 (2025 Q12)
      STUDENT_2026_PT1_Q11: data.STUDENT_PT1_Q12 || "",

      // Language - Q12 (2025 Q15)
      STUDENT_2026_PT1_Q12: data.STUDENT_PT1_Q15 || "",

      // Living Place - Q13 (2025 Q9)
      STUDENT_2026_PT1_Q13: data.STUDENT_PT1_Q9 || "",

      // Married - Q14 (2025 Q8)
      STUDENT_2026_PT1_Q14: data.STUDENT_PT1_Q8 || "",

      // Guruji Name - Q15 (2025 Q13)
      STUDENT_2026_PT1_Q15: data.STUDENT_PT1_Q13 || "",

      // Guruji Mobile - Q16 (2025 Q14)
      STUDENT_2026_PT1_Q16: data.STUDENT_PT1_Q14 || "",

      // Adhit Ved - Q17_A (2025 Q16)
      STUDENT_2026_PT1_Q17_A: data.STUDENT_PT1_Q16 || "",

      // Adhit Vedshakha - Q17_B (2025 Q17)
      STUDENT_2026_PT1_Q17_B: data.STUDENT_PT1_Q17 || "",

      // Swa-ved - Q18 (2025 Q18)
      STUDENT_2026_PT1_Q18: data.STUDENT_PT1_Q18 || "",

      // Extra Shakha - Q19 (2025 Q19)
      STUDENT_2026_PT1_Q19: data.STUDENT_PT1_Q19 || "",

      // Exam Details - Q20 (Matches 2025)
      STUDENT_2026_PT1_Q20_A: data.STUDENT_PT1_Q20_EXAM_NAME || "",
      STUDENT_2026_PT1_Q20_B: data.STUDENT_PT1_Q20_EXAM_YEAR
        ? dayjs(data.STUDENT_PT1_Q20_EXAM_YEAR, "YYYY").isValid()
          ? dayjs(data.STUDENT_PT1_Q20_EXAM_YEAR, "YYYY")
          : null
        : null,
      STUDENT_2026_PT1_Q20_C: data.STUDENT_PT1_Q20_EXAM_CATEGORY || "",
      STUDENT_2026_PT1_Q20_D: data.STUDENT_PT1_Q20_EXAM_PLACE || "",
      // Certificate (File) - skipped as per standard practice (clean upload needed)
      // STUDENT_2026_PT1_Q20_EXAM_CERTIFICATE: "",

      // Previous Guruji/School - Q21 (2025 Q21)
      STUDENT_2026_PT1_Q21_A: data.STUDENT_PT1_Q21 || "",
      STUDENT_2026_PT1_Q21_B: data.STUDENT_PT1_Q22 || "",

      // Previous Study - Q22 (2025 Q22)
      STUDENT_2026_PT1_Q22: data.STUDENT_PT1_Q23 || "",

      // Future Study Level - Q23 (2025 Q23)
      STUDENT_2026_PT1_Q23: data.STUDENT_PT1_Q24 || "",

      STUDENT_2026_PT1_Q24: data.STUDENT_PT1_Q25 || "",
      STUDENT_2026_PT1_Q25: data.STUDENT_PT1_Q26 || "",
      STUDENT_2026_PT1_Q26: data.STUDENT_PT1_Q27 || "",
      STUDENT_2026_PT1_Q27: data.STUDENT_PT1_Q28 || "",
      STUDENT_2026_PT1_Q28: data.STUDENT_PT1_Q30 || "",
      STUDENT_2026_PT1_Q29: data.STUDENT_PT1_Q31 || "",
      STUDENT_2026_PT1_Q32: data.STUDENT_PT1_Q30 || "",
    };

    // Filter out null/undefined values
    return Object.fromEntries(
      Object.entries(mappedData).filter(
        ([_, value]) => value !== null && value !== undefined && value !== "",
      ),
    );
  };

  const getPreviousFormData = async () => {
    setIsLoading(true);
    try {
      const payload: any = {
        data: {
          part: eSTAGE.STAGE1,
          year: CURRENT_YEAR - 1,
          userFormType: eUSER_FORM_TYPE.STUDENT,
          userId: userId,
          formFor: formFor,
        },
      };

      const response = await post(GetFormData, payload);
      const { dataResponse, data } = response;
      const { returnCode } = dataResponse;

      if (returnCode == eResultCode.SUCCESS) {
        const formData = Object.values(data?.studentPt1Forms || {}).every(
          (value) => value === null,
        )
          ? {}
          : data?.studentPt1Forms || {};

        // List of fields to exclude (file input fields)
        const jsonStringFields: string[] = [];

        const fileFields: string[] = [
          "STUDENT_PT1_Q29",
          "STUDENT_PT1_Q20_EXAM_CERTIFICATE",
        ];

        const formattedData = Object.keys(formData).reduce(
          (acc: { [key: string]: any }, key) => {
            if (jsonStringFields.includes(key)) {
              try {
                acc[key] = formData[key] ? JSON.parse(formData[key]) : [{}];
              } catch (error) {
                console.error(error);
              }
            } else if (fileFields.includes(key)) {
              acc[key] = "";
            } else {
              acc[key] = formData[key];
            }
            return acc;
          },
          {},
        );

        const mappedData = getMappedData(formattedData);

        console.log("Previous Year Data (2025):", formattedData);
        console.log("Mapped to 2026 Fields:", mappedData);

        // Use mapped data (with 2026 field names) instead of original formattedData
        if (mappedData && Object.keys(mappedData).length > 0) {
          form.setFieldsValue(mappedData);
        }

        // Don't set documents from previous year - let current year handle it
        // setDocumentsData(data?.documents);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (formId && formFor && userId) {
      const fetchData = async () => {
        await getPreviousFormData();
        await getFormData();

        let specificUserData: any = null;
        if (userId && Number(formFor) === eFORM_FOR.OTHER) {
          try {
            const userResponse = await post(GetSpecificUserData, {
              data: { id: userId },
            });
            if (
              userResponse?.dataResponse?.returnCode === eResultCode.SUCCESS &&
              userResponse?.data
            ) {
              specificUserData = userResponse.data;
            }
          } catch (error) {
            console.error("Error fetching specific user data:", error);
          }
        }

        const userDataToUse = specificUserData || user;

        // Auto-fill logic: Run AFTER fetching data to avoid overwriting existing user input.
        const autoFillData: { [key: string]: any } = {
          STUDENT_2026_PT1_Q1: userDataToUse?.fullName,
          STUDENT_2026_PT1_Q27: userDataToUse?.fullName,
          STUDENT_2026_PT1_Q28: userDataToUse?.fullName,
          STUDENT_2026_PT1_Q7: userDataToUse?.aadhaarCardNumber,
          STUDENT_2026_PT1_Q4: userDataToUse?.dateOfBirth
            ? dayjs(userDataToUse.dateOfBirth, "YYYY-MM-DD")
            : null,
          STUDENT_2026_PT1_Q2: userDataToUse?.fatherName,
          STUDENT_2026_PT1_Q10: userDataToUse?.mobileNumber,
          STUDENT_2026_PT1_Q11: userDataToUse?.emailId,
        };

        const fieldsToSet: { [key: string]: any } = {};
        const bulkPayload: any[] = [];

        Object.entries(autoFillData).forEach(([key, value]) => {
          // Check if field is currently empty in the form
          const currentValue = form.getFieldValue(key);
          if (
            !currentValue &&
            value !== undefined &&
            value !== null &&
            value !== ""
          ) {
            // Set value for Form State
            fieldsToSet[key] = value;

            // Prepare value for Backend Save
            let answerStr = String(value);
            if (dayjs.isDayjs(value)) {
              answerStr = value.isValid() ? value.format("YYYY-MM-DD") : "";
            }

            if (answerStr) {
              bulkPayload.push({
                questionKey: key,
                answerType: eAnswerType.TEXT,
                answer: answerStr,
                part: eSTAGE.STAGE1,
              });
            }
          }
        });

        if (bulkPayload.length > 0) {
          form.setFieldsValue(fieldsToSet);
          handleFieldChange("", "", eAnswerType.TEXT, bulkPayload);
        }

        const affidavitField = "STUDENT_PT1_Q30";
        const currentVal = form.getFieldValue(affidavitField);
        if (!currentVal) {
          const today = dayjs();
          form.setFieldValue(affidavitField, today);
          handleFieldChange(affidavitField, today, eAnswerType.DATE);
        }
      };
      fetchData();
    }
  }, [formId, formFor, userId, user]);

  const LengthCounter: React.FC<{ form: any; fieldName: string }> = ({
    form,
    fieldName,
  }) => {
    // Use useWatch to get real-time updates for the field value
    const fieldValue = Form.useWatch(fieldName, form);

    return (
      <div className='length_counter'>
        {(fieldValue || "").length}/{INPUT_LENGTH.TEXTAREA_LENGTH}
      </div>
    );
  };

  // Initialize auto-save hook
  const { debouncedSave, handleFormSubmit, onFormValuesChange } =
    useFormAutoSave({
      formId,
      form,
      stage: eSTAGE.STAGE1,
      handleFieldChange,
      excludeFields: ["STUDENT_2026_PT1_Q31", "STUDENT_2026_PT1_Q20_E"], // Exclude file upload fields
    });

  // Centralized handler for local side-effects + auto-save
  const handleLocalFormChange = (changedValues: any, allValues: any) => {
    // 1. Run Auto Save Logic from hook
    onFormValuesChange(changedValues, allValues);

    // 2. Handle Side Effects

    // Q1 Side Effect: Copy Name to Q27, Q28
    if ("STUDENT_2026_PT1_Q1" in changedValues) {
      const value = changedValues.STUDENT_2026_PT1_Q1;
      if (value) {
        form.setFieldValue("STUDENT_2026_PT1_Q27", value);
        form.setFieldValue("STUDENT_2026_PT1_Q28", value);
        debouncedSave("STUDENT_2026_PT1_Q27", value);
        debouncedSave("STUDENT_2026_PT1_Q28", value);
      }
    }

    // Same Address Side Effect: Copy Q8 to Q9 if checkbox is checked
    if ("STUDENT_2026_PT1_Q8" in changedValues && sameAsCurrentAddress) {
      const value = changedValues.STUDENT_2026_PT1_Q8;
      form.setFieldValue("STUDENT_2026_PT1_Q9", value);
      debouncedSave("STUDENT_2026_PT1_Q9", value);
    }
  };

  /**
   * Handle "Next" button click - validates form and enters review mode
   */
  const handleNextClick = async () => {
    try {
      await form.validateFields();
      setIsReviewMode(true);
      // Scroll to top so user can review from beginning
      document
        .getElementById("studentFormPart1")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    } catch (errorInfo: any) {
      if (errorInfo?.errorFields?.length > 0) {
        form.scrollToField(errorInfo.errorFields[0].name, {
          behavior: "smooth",
        });
      }
      console.log("Validation failed:", errorInfo);
    }
  };

  /**
   * Handle "Back" button click - exits review mode to allow editing
   */
  const handleBackClick = () => {
    setIsReviewMode(false);
  };

  /**
   * Handle "Same as Current Address" checkbox change
   */
  const handleSameAddressChange = useCallback(
    (checked: boolean) => {
      setSameAsCurrentAddress(checked);
      if (checked) {
        const correspondenceAddress = form.getFieldValue("STUDENT_2026_PT1_Q8");
        form.setFieldValue("STUDENT_2026_PT1_Q9", correspondenceAddress);
        handleFieldChange("STUDENT_2026_PT1_Q9", correspondenceAddress || "");
      }
    },
    [form, handleFieldChange],
  );

  useEffect(() => {
    setSamvedShakhaOptions(() => {
      return Object.values(vedMapping).flatMap((item) => item.vedashakhas);
    });
  }, []);

  return (
    <div
      id='studentFormPart1'
      className={`${styles.formWrapper} studentTeacherSchoolFormWrapper`}
    >
      <Form
        {...formItemLayout}
        form={form}
        name='register'
        onFinish={handleFormSubmit}
        onValuesChange={handleLocalFormChange}
        initialValues={{
          STUDENT_PT1_Q16: null,
          STUDENT_PT1_Q17: null,
        }}
        scrollToFirstError
        layout='vertical'
      >
        {/* Form */}
        <Row>
          <Col>
            <Typography.Title level={3}>
              {Text({
                tid: "STUDENT_2026_PART_1_FORM_HEADING",
                def: "STUDENT_PART_1_FORM_HEADING",
              })}
            </Typography.Title>
            {!disabled && (
              <Typography.Text type='danger'>
                {Text({
                  tid: "all_fields_are_mandatory",
                  def: "All fields are mandatory!",
                })}
              </Typography.Text>
            )}
          </Col>
        </Row>
          {isReviewMode && (
                    <Row style={{ marginBottom: 10 }}>
                      <Typography.Text type='warning'>
                        <CheckCircleOutlined style={{ marginRight: 6 }} />
                        {Text({
                          tid: "REVIEW_MODE_INSTRUCTION",
                          def: "You are in review mode. You can only view the submitted data. To make changes, please switch to edit mode.",
                        })}
                      </Typography.Text>
                    </Row>
                  )}
        {/* 1. Name */}
        <Row>
          <Col
            xs={24}
            lg={12}
            xl={8}
          >
            <Form.Item
              name='STUDENT_2026_PT1_Q1'
              label={Text({
                tid: "STUDENT_2026_PT1_Q1",
                def: "Name",
              })}
              rules={[
                {
                  required: true,
                  message: Text({ tid: "REQUIRED", def: "Required" }),
                },
              ]}
            >
              <Input
                placeholder={Text({
                  tid: "STUDENT_2026_PT1_Q1_PLACEHOLDER",
                  def: "Ex. Devvrat Sharma",
                })}
                disabled={disabled || isReviewMode}
                onChange={(e) => {
                  form.setFieldValue("STUDENT_2026_PT1_Q28", e.target.value);
                  form.setFieldValue("STUDENT_2026_PT1_Q30", e.target.value);
                }}
              />
            </Form.Item>
          </Col>
        </Row>
        {/* 2. Father's name */}
        <Row>
          <Col
            xs={24}
            lg={12}
            xl={8}
          >
            <Form.Item
              name='STUDENT_2026_PT1_Q2'
              label={Text({
                tid: "STUDENT_2026_PT1_Q2",
                def: "Father's name",
              })}
              rules={[
                {
                  required: true,
                  message: Text({ tid: "REQUIRED", def: "Required" }),
                },
              ]}
            >
              <Input
                placeholder={Text({
                  tid: "STUDENT_2026_PT1_Q2_PLACEHOLDER",
                  def: "Ex. Naresh Sharma",
                })}
                disabled={disabled || isReviewMode}
              />
            </Form.Item>
          </Col>
        </Row>
        {/* 3. Mother's name */}
        <Row>
          <Col
            xs={24}
            lg={12}
            xl={8}
          >
            <Form.Item
              name='STUDENT_2026_PT1_Q3'
              label={Text({
                tid: "STUDENT_2026_PT1_Q3",
                def: "Mother's name",
              })}
              rules={[
                {
                  required: true,
                  message: Text({ tid: "REQUIRED", def: "Required" }),
                },
              ]}
            >
              <Input
                placeholder={Text({
                  tid: "STUDENT_2026_PT1_Q3_PLACEHOLDER",
                  def: "Ex. Sumitra Sharma",
                })}
                disabled={disabled || isReviewMode}
              />
            </Form.Item>
          </Col>
        </Row>
        {/* 4. DOB */}
        <Row>
          <Col
            xs={24}
            lg={12}
            xl={8}
          >
            <Form.Item
              name='STUDENT_2026_PT1_Q4'
              label={Text({
                tid: "STUDENT_2026_PT1_Q4",
                def: "Date of birth",
              })}
              rules={[
                {
                  required: true,
                  message: Text({ tid: "REQUIRED", def: "Required" }),
                },
              ]}
            >
              <DatePicker
                format='DD-MM-YYYY'
                placeholder={Text({
                  tid: "STUDENT_2026_PT1_Q4_PLACEHOLDER",
                  def: "Ex. 21-05-2001",
                })}
                disabledDate={(current) =>
                  current && current > dayjs().endOf("day")
                }
                disabled={disabled || isReviewMode}
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
        </Row>
        {/* 5. Birth Place */}
        <Row>
          <Col
            xs={24}
            lg={12}
            xl={8}
          >
            <Form.Item
              name='STUDENT_2026_PT1_Q5'
              label={Text({
                tid: "STUDENT_2026_PT1_Q5",
                def: "Birth Place",
              })}
              rules={[
                {
                  required: true,
                  message: Text({ tid: "REQUIRED", def: "Required" }),
                },
              ]}
            >
              <Input
                placeholder={Text({
                  tid: "STUDENT_2026_PT1_Q5_PLACEHOLDER",
                  def: "Ex. Varanasi",
                })}
                disabled={disabled || isReviewMode}
              />
            </Form.Item>
          </Col>
        </Row>
        {/* 6. Rishi-Gotra */}
        <Row>
          <Col
            xs={24}
            lg={12}
            xl={8}
          >
            <Form.Item
              name='STUDENT_2026_PT1_Q6'
              label={Text({
                tid: "STUDENT_2026_PT1_Q6",
                def: "Rishi-Gotra",
              })}
              rules={[
                {
                  required: true,
                  message: Text({ tid: "REQUIRED", def: "Required" }),
                },
              ]}
            >
              <Input
                placeholder={Text({
                  tid: "STUDENT_2026_PT1_Q6_PLACEHOLDER",
                  def: "Ex. Bharadwaj",
                })}
                disabled={disabled || isReviewMode}
              />
            </Form.Item>
          </Col>
        </Row>
        {/* 7. Aadhaar Card Number */}
        <Row>
          <Col
            xs={24}
            lg={12}
            xl={8}
          >
            <Form.Item
              name='STUDENT_2026_PT1_Q7'
              label={Text({
                tid: "STUDENT_2026_PT1_Q7",
                def: "Aadhaar No.",
              })}
              rules={[
                {
                  required: true,
                  message: Text({ tid: "REQUIRED", def: "Required" }),
                },
                {
                  pattern: /^[2-9]{1}[0-9]{11}$/, // Aadhaar must be 12 digits and should not start with 0 or 1
                  message: Text({
                    tid: "aadhaar_card_validation",
                    def: "Aadhaar must be 12 digits and should not start with 0 or 1",
                  }),
                },
              ]}
            >
              <Input
                placeholder={Text({
                  tid: "STUDENT_2026_PT1_Q7_PLACEHOLDER",
                  def: "0123 4567 8910",
                })}
                maxLength={12}
                onKeyDown={allowOnlyNumericInput_onKeyDown}
                disabled={disabled || isReviewMode}
              />
            </Form.Item>
          </Col>
        </Row>
        {/* 8. Address for correspondence with PIN code */}
        <Row>
          <Col
            xs={24}
            lg={12}
            xl={12}
          >
            <Form.Item
              name='STUDENT_2026_PT1_Q8'
              label={Text({
                tid: "STUDENT_2026_PT1_Q8",
                def: "Address for correspondence with PIN code",
              })}
              rules={[
                {
                  required: true,
                  message: Text({ tid: "REQUIRED", def: "Required" }),
                },
              ]}
            >
              <>
                <Input.TextArea
                  placeholder={Text({
                    tid: "STUDENT_2026_PT1_Q8_PLACEHOLDER",
                    def: "Ex. House no. 51, Hanuman Ghat, Varanasi, Uttar Pradesh, PIN-221001",
                  })}
                  disabled={disabled || isReviewMode}
                  maxLength={INPUT_LENGTH.TEXTAREA_LENGTH}
                  style={{ paddingBottom: 20 }}
                />
              </>
            </Form.Item>
            <LengthCounter
              form={form}
              fieldName='STUDENT_2026_PT1_Q8'
            />
          </Col>
        </Row>

        {!disabled && (
          <div style={{ marginTop: 6 }}>
            <Checkbox
              checked={sameAsCurrentAddress}
              onChange={(e) =>
                handleSameAddressChange(e.target.checked)
              }
              disabled={isReviewMode}
            >
              {Text({
                tid: "SAME_AS_CORRESPONDENCE_ADDRESS",
                def: "Same as correspondence address",
              })}
            </Checkbox>
          </div>
        )}
        {/* 9. Permanent Address with PIN code */}
        <Row>
          <Col
            xs={24}
            lg={12}
            xl={12}
          >
            <Form.Item
              name='STUDENT_2026_PT1_Q9'
              label={
                <>
                  <span>
                    {Text({
                      tid: "STUDENT_2026_PT1_Q9",
                      def: "Permanent Address with PIN code",
                    })}
                  </span>
                </>
              }
              rules={[
                {
                  required: true,
                  message: Text({ tid: "REQUIRED", def: "Required" }),
                },
              ]}
            >
              <Input.TextArea
                placeholder={Text({
                  tid: "STUDENT_2026_PT1_Q9_PLACEHOLDER",
                  def: "Ex. House no. 51, Hanuman Ghat, Varanasi, Uttar Pradesh, PIN-221001",
                })}
                disabled={disabled || isReviewMode || sameAsCurrentAddress}
                maxLength={INPUT_LENGTH.TEXTAREA_LENGTH}
                style={{ paddingBottom: 20 }}
              />
            </Form.Item>
            <LengthCounter
              form={form}
              fieldName='STUDENT_2026_PT1_Q9'
            />
          </Col>
        </Row>
        {/* 10. Your mobile number */}
        <Row>
          <Col
            xs={24}
            lg={12}
            xl={8}
          >
            <Form.Item
              name='STUDENT_2026_PT1_Q10'
              label={Text({
                tid: "STUDENT_2026_PT1_Q10",
                def: "Your mobile number",
              })}
              rules={[
                {
                  required: true,
                  message: Text({ tid: "REQUIRED", def: "Required" }),
                },
                {
                  pattern: Regex.MOBILENO, // Updated pattern
                  message: Text({
                    tid: "mobile_number_validation",
                    def: "Enter a valid 10-digit mobile number",
                  }),
                },
              ]}
            >
              <Input
                placeholder={Text({
                  tid: "STUDENT_2026_PT1_Q10_PLACEHOLDER",
                  def: "7357658777",
                })}
                maxLength={10}
                disabled={disabled || isReviewMode}
                onKeyDown={allowOnlyNumericInput_onKeyDown}
              />
            </Form.Item>
          </Col>
        </Row>
        {/* 11. Your email ID */}
        <Row>
          <Col
            xs={24}
            lg={12}
            xl={8}
          >
            <Form.Item
              name='STUDENT_2026_PT1_Q11'
              label={Text({
                tid: "STUDENT_2026_PT1_Q11",
                def: "Your email ID",
              })}
              rules={[
                {
                  required: true,
                  message: Text({ tid: "REQUIRED", def: "Required" }),
                },
                {
                  pattern: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                  message: Text({
                    tid: "VALID_EMAIL",
                    def: "VALID_EMAIL",
                  }),
                },
              ]}
            >
              <Input
                type='email'
                placeholder={Text({
                  tid: "STUDENT_2026_PT1_Q11_PLACEHOLDER",
                  def: "applications@bharatatmapuraskar.org",
                })}
                disabled={disabled || isReviewMode}
              />
            </Form.Item>
          </Col>
        </Row>
        {/* 11 In which language should the relevant conversation be conducted with you? */}
        <Row>
          <Col
            xs={24}
            lg={24}
            xl={24}
          >
            <Form.Item
              name='STUDENT_2026_PT1_Q12'
              label={Text({
                tid: "STUDENT_2026_PT1_Q12",
                def: "In which language should the relevant conversation be conducted with you?",
              })}
              rules={[
                {
                  required: true,
                  message: Text({ tid: "REQUIRED", def: "Required" }),
                },
              ]}
            >
              <Radio.Group disabled={disabled || isReviewMode}>
                <Radio value='English'>
                  {Text({ tid: "ENGLISH", def: "English" })}
                </Radio>
                <Radio value='हिन्दी'>
                  {Text({ tid: "HINDI", def: "Hindi" })}
                </Radio>
                <Radio value='संस्कृतम्'>
                  {Text({ tid: "SANSKRIT", def: "Sanskrit" })}
                </Radio>
              </Radio.Group>
            </Form.Item>
          </Col>
        </Row>
        {/* 12. Where do you live? (select one) */}
        <Row>
          <Col
            xs={24}
            lg={24}
            xl={24}
          >
            <Form.Item
              name='STUDENT_2026_PT1_Q13'
              label={Text({
                tid: "STUDENT_2026_PT1_Q13",
                def: "Where do you live? (select one)",
              })}
              rules={[
                {
                  required: true,
                  message: Text({ tid: "REQUIRED", def: "Required" }),
                },
              ]}
            >
              <Radio.Group disabled={disabled || isReviewMode}>
                <Radio value='VEDA_SCHOOL'>
                  {Text({ tid: "VEDA_SCHOOL", def: "VEDA_SCHOOL" })}
                </Radio>
                <Radio value='GURUKUL'>
                  {Text({ tid: "GURUKUL", def: "GURUKUL" })}
                </Radio>
                <Radio value='PATRIARCHAL_HOME'>
                  {Text({ tid: "PATRIARCHAL_HOME", def: "PATRIARCHAL_HOME" })}
                </Radio>
              </Radio.Group>
            </Form.Item>
          </Col>
        </Row>
        {/* 14. Are you married? */}
        <Row>
          <Col
            xs={24}
            lg={24}
            xl={24}
          >
            <Form.Item
              name='STUDENT_2026_PT1_Q14'
              label={Text({
                tid: "STUDENT_2026_PT1_Q14",
                def: "Are you married?",
              })}
              rules={[
                {
                  required: true,
                  message: Text({ tid: "REQUIRED", def: "Required" }),
                },
              ]}
            >
              <Radio.Group disabled={disabled || isReviewMode}>
                <Radio value={eYES_OR_NO.YES}>
                  {Text({ tid: "MARRIED", def: "MARRIED" })}
                </Radio>
                <Radio value={eYES_OR_NO.NO}>
                  {Text({ tid: "UNMARRIED", def: "UNMARRIED" })}
                </Radio>
              </Radio.Group>
            </Form.Item>
          </Col>
        </Row>

        {/* 15. Guru ka naam: */}
        <Row>
          <Col
            xs={24}
            lg={12}
            xl={8}
          >
            <Form.Item
              name='STUDENT_2026_PT1_Q15'
              label={Text({
                tid: "STUDENT_2026_PT1_Q15",
                def: "Current teacher of the Ved / Guru’s name:",
              })}
              rules={[
                {
                  required: true,
                  message: Text({ tid: "REQUIRED", def: "Required" }),
                },
              ]}
            >
              <Input
                disabled={disabled || isReviewMode}
                placeholder={Text({
                  tid: "STUDENT_2026_PT1_Q15_PLACEHOLDER",
                  def: "Ex. Naresh Sharma",
                })}
              />
            </Form.Item>
          </Col>
        </Row>
        {/* 16. Guru ka mobile number: */}
        <Row>
          <Col
            xs={24}
            lg={12}
            xl={8}
          >
            <Form.Item
              name='STUDENT_2026_PT1_Q16'
              label={Text({
                tid: "STUDENT_2026_PT1_Q16",
                def: "Guru’s mobile number:",
              })}
              rules={[
                {
                  required: true,
                  message: Text({ tid: "REQUIRED", def: "Required" }),
                },
                {
                  pattern: Regex.MOBILENO, // Updated pattern
                  message: Text({
                    tid: "mobile_number_validation",
                    def: "Enter a valid 10-digit mobile number",
                  }),
                },
              ]}
            >
              <Input
                maxLength={10}
                disabled={disabled || isReviewMode}
                onKeyDown={allowOnlyNumericInput_onKeyDown}
                placeholder={Text({
                  tid: "STUDENT_2026_PT1_Q16_PLACEHOLDER",
                  def: "7357658777",
                })}
              />
            </Form.Item>
          </Col>
        </Row>
        {/* 17. Adhit Ved: */}
        <>
          {/* 17_A. Adhit Ved: */}
          <Row>
            <Col
              xs={24}
              lg={12}
              xl={8}
            >
              <Form.Item
                name='STUDENT_2026_PT1_Q17_A'
                label={
                  <>
                    {Text({
                      tid: "STUDENT_2026_PT1_Q17_A",
                      def: "अधीत वेद :",
                    })}
                    <FormHelpTooltip
                      title={Text({
                        tid: "DROPDOWN_SELECTION_HELP",
                        def: "Please choose an option from the dropdown",
                      })}
                    />
                  </>
                }
                rules={[
                  {
                    required: true,
                    message: Text({ tid: "REQUIRED", def: "Required" }),
                  },
                ]}
              >
                <Select
                  disabled={disabled || isReviewMode}
                  onChange={(value) => {
                    handleFieldChange("STUDENT_2026_PT1_Q17_A", value);
                    form.setFieldsValue({ STUDENT_2026_PT1_Q17_B: "" });
                  }}
                  placeholder={DROPDOWN_PLACEHOLDER}
                >
                  {Object.keys(vedMapping).map((vedKey) => (
                    <Select.Option
                      key={vedKey}
                      value={vedMapping[vedKey].label}
                    >
                      {vedMapping[vedKey].label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          {/* 17_B. Adhit Vedshakha : */}
          <Row className='QuestionsSubSection'>
            <Col
              xs={24}
              lg={12}
              xl={8}
            >
              <Form.Item
                name='STUDENT_2026_PT1_Q17_B'
                label={
                  <>
                    {Text({
                      tid: "STUDENT_2026_PT1_Q17_B",
                      def: "Adhit Vedshakha :",
                    })}
                    <FormHelpTooltip
                      title={Text({
                        tid: "DROPDOWN_SELECTION_HELP",
                        def: "Please choose an option from the dropdown",
                      })}
                    />
                  </>
                }
                rules={[
                  {
                    required: true,
                    message: Text({ tid: "REQUIRED", def: "Required" }),
                  },
                ]}
              >
                <Select
                  disabled={disabled || isReviewMode}
                  onChange={(value) =>
                    handleFieldChange("STUDENT_2026_PT1_Q17_B", value)
                  }
                  placeholder={DROPDOWN_PLACEHOLDER}
                >
                  {form.getFieldValue("STUDENT_2026_PT1_Q17_A") &&
                    (() => {
                      const selectedVedLabel = form.getFieldValue(
                        "STUDENT_2026_PT1_Q17_A",
                      );
                      const vedEntry = Object.values(vedMapping).find(
                        (ved: any) => ved.label === selectedVedLabel,
                      );
                      return vedEntry?.vedashakhas.map((vedashakha: any) => (
                        <Select.Option
                          key={vedashakha}
                          value={vedashakha}
                        >
                          {vedashakha}
                        </Select.Option>
                      ));
                    })()}
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </>
        {/* 18. swa-ved shakha: */}
        <Row>
          <Col
            xs={24}
            lg={12}
            xl={8}
          >
            <Form.Item
              name='STUDENT_2026_PT1_Q18'
              label={
                <>
                  {Text({
                    tid: "STUDENT_2026_PT1_Q18",
                    def: "Swa-ved shakha :",
                  })}
                  <FormHelpTooltip
                    title={Text({
                      tid: "DROPDOWN_SELECTION_HELP",
                      def: "Please choose an option from the dropdown",
                    })}
                  />
                </>
              }
            >
              <Select
                disabled={disabled || isReviewMode}
                onChange={(value) =>
                  handleFieldChange("STUDENT_2026_PT1_Q18", value)
                }
                placeholder={DROPDOWN_PLACEHOLDER}
              >
                {samvedShakhaOptions &&
                  samvedShakhaOptions.map((item: any) => (
                    <Select.Option
                      key={item}
                      value={item}
                    >
                      {item}
                    </Select.Option>
                  ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
        {/* 19. यदि एक से अधिक वेदशाखा का अध्ययन किया है तो वेदशाखा के नाम */}
        <Row>
          <Col
            xs={24}
            lg={12}
            xl={12}
          >
            <Form.Item
              name='STUDENT_2026_PT1_Q19'
              label={Text({
                tid: "STUDENT_2026_PT1_Q19",
                def: "यदि एक से अधिक वेदशाखा का अध्ययन किया है तो वेदशाखा के नाम",
              })}
            >
              <Input
                disabled={disabled || isReviewMode}
                placeholder={Text({
                  tid: "STUDENT_2026_PT1_Q19_PLACEHOLDER",
                  def: "Ex. Madhyandinashakha",
                })}
              />
            </Form.Item>
          </Col>
        </Row>
        {/* 20. QUESTION 20 HEADING : */}
        <>
          <Row>
            <Col>
              <Typography.Text>
                {Text({
                  tid: "STUDENT_2026_PT1_Q20_HEADING",
                  def: "Highest exam level passed in Ved studies",
                })}
              </Typography.Text>
            </Col>
          </Row>
          {/* QUESTION 20 QUESTIONS EXAM_NAME, EXAM_YEAR, EXAM_CATEGORY, EXAM_PLACE, CERTIFICATE */}
          <Row className='QuestionsSubSection'>
            <Col
              xs={24}
              lg={12}
              xl={12}
            >
              {/* EXAM_NAME */}
              <Form.Item
                name='STUDENT_2026_PT1_Q20_A'
                label={Text({
                  tid: "STUDENT_2026_PT1_Q20_A",
                  def: "Exam name :",
                })}
                rules={[
                  {
                    required: true,
                    message: Text({ tid: "REQUIRED", def: "Required" }),
                  },
                ]}
              >
                <Input
                  disabled={disabled || isReviewMode}
                  placeholder={Text({
                    tid: "STUDENT_2026_PT1_Q20_A_PLACEHOLDER",
                    def: "Ex. Salakshana Ghananta",
                  })}
                />
              </Form.Item>
              {/* EXAM_YEAR */}
              <Form.Item
                name='STUDENT_2026_PT1_Q20_B'
                label={Text({
                  tid: "STUDENT_2026_PT1_Q20_B",
                  def: "Exam year :",
                })}
                rules={[
                  {
                    required: true,
                    message: Text({ tid: "REQUIRED", def: "Required" }),
                  },
                ]}
              >
                <DatePicker
                  disabled={disabled || isReviewMode}
                  format='MM/YYYY'
                  onKeyDown={(e) => e.preventDefault()}
                  picker='month'
                  placeholder={Text({
                    tid: "STUDENT_2026_PT1_Q20_B_PLACEHOLDER",
                    def: "Ex. June-2025",
                  })}
                />
              </Form.Item>
              {/* EXAM_CATEGORY */}
              <Form.Item
                name='STUDENT_2026_PT1_Q20_C'
                label={Text({
                  tid: "STUDENT_2026_PT1_Q20_C",
                  def: "Exam category :",
                })}
                rules={[
                  {
                    required: true,
                    message: Text({ tid: "REQUIRED", def: "Required" }),
                  },
                ]}
              >
                <Input
                  disabled={disabled || isReviewMode}
                  placeholder={Text({
                    tid: "STUDENT_2026_PT1_Q20_C_PLACEHOLDER",
                    def: "Ex. First",
                  })}
                />
              </Form.Item>
              {/* EXAM_PLACE */}
              <Form.Item
                name='STUDENT_2026_PT1_Q20_D'
                label={
                  <>
                    {Text({
                      tid: "STUDENT_2026_PT1_Q20_D",
                      def: "Exam place :",
                    })}
                    <FormHelpTooltip
                      title={Text({
                        tid: "DROPDOWN_SELECTION_HELP",
                        def: "Please choose an option from the dropdown",
                      })}
                    />
                  </>
                }
                rules={[
                  {
                    required: true,
                    message: Text({ tid: "REQUIRED", def: "Required" }),
                  },
                ]}
              >
                <Select
                  disabled={disabled || isReviewMode}
                  placeholder={Text({
                    tid: "SELECT_OPTION",
                    def: "Select",
                  })}
                  onChange={(value) =>
                    handleFieldChange("STUDENT_2026_PT1_Q20_D", value)
                  }
                >
                  {EXAM_INSTITUTE_LIST.map((item) => (
                    <Select.Option
                      key={item.value}
                      value={item.value}
                    >
                      {Text({
                        tid: item.value,
                        def: item.label,
                      })}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              {isInstituteOther === "INSTITUTE_OTHER" && (
                <Form.Item
                  name='STUDENT_2026_PT1_Q20_D_OTHER'
                  label={Text({
                    tid: "OTHER_INSTITUTE",
                    def: "Institute Name",
                  })}
                  rules={[
                    {
                      required: true,
                      message: Text({ tid: "REQUIRED", def: "Required" }),
                    },
                  ]}
                >
                  <Input disabled={disabled || isReviewMode} />
                </Form.Item>
              )}
              <div style={{paddingLeft: "25px"}}>
                <DocumentUploadField
                  fieldName='STUDENT_2026_PT1_Q20_E'
                  label={Text({
                    tid: "STUDENT_2026_PT1_Q20_E",
                    def: "Exam certificate :",
                  })}
                formId={formId || ""}
                labelStyle={{paddingLeft : "0"}}
                  userId={user?.id || ""}
                  currentStage={eFORMSTATUS.STAGE1INPROGRESS}
                  disabled={disabled || isReviewMode}
                  existingDocuments={documentsData}
                  onUploadComplete={(docId) =>
                    handleFieldChange(
                      "STUDENT_2026_PT1_Q20_E",
                      docId,
                      eAnswerType.FILE,
                    )
                  }
                />
              </div>
            </Col>
          </Row>
        </>
        {/* 21. अपने वर्तमान / अन्तिम वेदाध्यापक व वेदविद्यालय के नाम जहाँ आपने वेदाध्ययन किया: */}
        <>
          <Row>
            <Col>
              <Typography.Text>
                {Text({
                  tid: "STUDENT_2026_PT1_Q21_HEADING",
                  def: "STUDENT_PT1_Q21_HEADING",
                })}
              </Typography.Text>
            </Col>
          </Row>
          <Row className='QuestionsSubSection'>
            <Col
              xs={24}
              lg={12}
              xl={12}
            >
              <Form.Item
                name='STUDENT_2026_PT1_Q21_A'
                label={Text({
                  tid: "STUDENT_2026_PT1_Q21_A",
                  def: "Teacher's name:",
                })}
                rules={[
                  {
                    required: true,
                    message: Text({ tid: "REQUIRED", def: "Required" }),
                  },
                ]}
              >
                <Input
                  disabled={disabled || isReviewMode}
                  placeholder={Text({
                    tid: "STUDENT_2026_PT1_Q21_A_PLACEHOLDER",
                    def: "Ex. Umesh Dwivedi",
                  })}
                />
              </Form.Item>
              <Form.Item
                name='STUDENT_2026_PT1_Q21_B'
                label={Text({
                  tid: "STUDENT_2026_PT1_Q21_B",
                  def: "School's name:",
                })}
                rules={[
                  {
                    required: true,
                    message: Text({ tid: "REQUIRED", def: "Required" }),
                  },
                ]}
              >
                <Input
                  disabled={disabled || isReviewMode}
                  placeholder={Text({
                    tid: "STUDENT_2026_PT1_Q21_B_PLACEHOLDER",
                    def: "Ex. Sanatan Vaidik Shikshan Sansthan, Kasar, Charbhuja, Rajasthan",
                  })}
                />
              </Form.Item>
            </Col>
          </Row>
        </>
        {/* 22. आप किस स्तर तक वेदाध्ययन करना चाहते हैं */}
        <Row>
          <Col xs={24}>
            <Form.Item
              name='STUDENT_2026_PT1_Q22'
              label={Text({
                tid: "STUDENT_2026_PT1_Q22",
                def: "Up to what level do you wish to study the Vedas?",
              })}
              rules={[
                {
                  required: true,
                  message: Text({ tid: "REQUIRED", def: "Required" }),
                },
              ]}
            >
              <>
                <Input.TextArea
                  disabled={disabled || isReviewMode}
                  maxLength={INPUT_LENGTH.TEXTAREA_LENGTH}
                  style={{ paddingBottom: 20 }}
                  placeholder={Text({
                    tid: "STUDENT_2026_PT1_Q22_PLACEHOLDER",
                    def: "Ex. Salakshana Ghananta with Shadanga",
                  })}
                />
              </>
            </Form.Item>
            <LengthCounter
              form={form}
              fieldName='STUDENT_2026_PT1_Q22'
            />
          </Col>
        </Row>
        {/* 23. क्या आप पौरोहित्य / अर्चकत्व में रुचि रखते हैं?*/}
        <Row>
          <Row>
            <Col
              xs={24}
              lg={24}
              xl={24}
            >
              <Form.Item
                name='STUDENT_2026_PT1_Q23'
                label={Text({
                  tid: "STUDENT_2026_PT1_Q23",
                  def: "23. Are you interested in Priesthood / Archakatva (temple service)?",
                })}
                rules={[
                  {
                    required: true,
                    message: Text({ tid: "REQUIRED", def: "Required" }),
                  },
                ]}
              >
                <Radio.Group disabled={disabled || isReviewMode}>
                  <Radio value={eYES_OR_NO.YES}>
                    {Text({ tid: "YES", def: "YES" })}
                  </Radio>
                  <Radio value={eYES_OR_NO.NO}>
                    {Text({ tid: "NO", def: "NO" })}
                  </Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
          </Row>
        </Row>
        {/* 24. आप आगे चलकर क्या बनना चाहते हैं */}
        <Row>
          <Col xs={24}>
            <Form.Item
              name='STUDENT_2026_PT1_Q24'
              label={Text({
                tid: "STUDENT_2026_PT1_Q24",
                def: "24. What do you aspire to become in the future?",
              })}
              rules={[
                {
                  required: true,
                  message: Text({ tid: "REQUIRED", def: "Required" }),
                },
              ]}
            >
              <>
                <Input.TextArea
                  disabled={disabled || isReviewMode}
                  maxLength={INPUT_LENGTH.TEXTAREA_LENGTH}
                  style={{ paddingBottom: 20 }}
                  placeholder={Text({
                    tid: "STUDENT_2026_PT1_Q24_PLACEHOLDER",
                    def: "Ex. Ideal Veda Teacher",
                  })}
                />
              </>
            </Form.Item>
            <LengthCounter
              form={form}
              fieldName='STUDENT_2026_PT1_Q24'
            />
          </Col>
        </Row>
        {/* 25. आप कौनसा व्यवसाय आजीविका के रूप में करना चाहते हैं? */}
        <Row>
          <Col xs={24}>
            <Form.Item
              name='STUDENT_2026_PT1_Q25'
              label={Text({
                tid: "STUDENT_2026_PT1_Q25",
                def: "25. What profession do you wish to pursue for livelihood?",
              })}
              rules={[
                {
                  required: true,
                  message: Text({ tid: "REQUIRED", def: "Required" }),
                },
              ]}
            >
              <>
                <Input
                  disabled={disabled || isReviewMode}
                  maxLength={INPUT_LENGTH.TEXTAREA_LENGTH}
                  style={{ paddingBottom: 20 }}
                  placeholder={Text({
                    tid: "STUDENT_2026_PT1_Q25_PLACEHOLDER",
                    def: "Ex. Veda Teaching",
                  })}
                />
              </>
            </Form.Item>
          </Col>
        </Row>
        {/* 26. आपको यह पुरस्कार क्यों दिया जाए? सप्रमाण पाँच प्रमुख कारण बतायें। (चार पंक्तियों अपना उत्तर दीजिये) */}
        <Row>
          <Col xs={24}>
            <Form.Item
              name='STUDENT_2026_PT1_Q26'
              label={Text({
                tid: "STUDENT_2026_PT1_Q26",
                def: "26. Why should you receive this award? Provide five key reasons with proof:",
              })}
              rules={[
                {
                  required: true,
                  message: Text({ tid: "REQUIRED", def: "Required" }),
                },
              ]}
            >
              <>
                <Input.TextArea
                  disabled={disabled || isReviewMode}
                  maxLength={INPUT_LENGTH.TEXTAREA_LENGTH}
                  style={{ paddingBottom: 20 }}
                  placeholder={Text({
                    tid: "STUDENT_2026_PT1_Q26_PLACEHOLDER",
                    def: "Ex. 1. After completing self-branch study, completed Salakshana Ghananta study of Shakalashakha.",
                  })}
                />
              </>
            </Form.Item>
            <LengthCounter
              form={form}
              fieldName='STUDENT_2026_PT1_Q26'
            />
          </Col>
        </Row>

        <div
          id='student_part_one_affidavit'
          className='affidavitSection'
        >
          <Row>
            <Col>
              <Typography.Text>
                {Text({
                  tid: "AFFIDAVIT_TITLE",
                  def: "AFFIDAVIT TITLE",
                })}
              </Typography.Text>
            </Col>
          </Row>
          <Row className='affidavitRow'>
            <Col xs={24}>
              {Text({ tid: "I", def: "I" })}
              <Form.Item
                name='STUDENT_2026_PT1_Q27'
                style={{
                  maxWidth: "200px",
                }}
                className='affidavitRowInput'
              >
                <Input disabled={true} />
              </Form.Item>
              {Text({
                tid: "student_affidavit_line_1",
                def: "",
              })}
            </Col>
          </Row>
          <Col xs={24}>
            <ul className='mb-label'>
              <li>
                <Typography.Text>
                  {Text({
                    tid: "AFFIDAVIT_CLAUSE_1",
                    def: "AFFIDAVIT_CLAUSE_1",
                  })}
                </Typography.Text>
              </li>

              <li>
                <Typography.Text type='secondary'>
                  {Text({
                    tid: "AFFIDAVIT_CLAUSE_2",
                    def: "AFFIDAVIT_CLAUSE_2",
                  })}
                </Typography.Text>
              </li>
              <li>
                <Typography.Text type='secondary'>
                  {Text({
                    tid: "AFFIDAVIT_CLAUSE_3",
                    def: "AFFIDAVIT_CLAUSE_3",
                  })}
                </Typography.Text>
              </li>
            </ul>

            <Typography.Text>
              {Text({
                tid: "AFFIDAVIT_VERIFICATION",
                def: "AFFIDAVIT_VERIFICATION",
              })}
            </Typography.Text>

            <Typography.Text>
              {Text({
                tid: "AFFIDAVIT_UNDERSTANDING",
                def: "AFFIDAVIT_UNDERSTANDING",
              })}
            </Typography.Text>
            {!disabled ? (
              <Form.Item
                name='affidavitAccepted'
                valuePropName='checked'
                rules={[
                  {
                    required: true,
                    transform: (value: boolean) => value || undefined,
                    type: "boolean",
                    message: Text({
                      tid: "AFFIDAVIT_ACCEPTANCE_REQUIRED",
                      def: "You must accept the affidavit declaration to submit the form",
                    }),
                  },
                ]}
                style={{ marginBottom: 0 }}
              >
                <Checkbox
                  checked={affidavitAccepted}
                  disabled={disabled || isReviewMode}
                >
                  {Text({
                    tid: "AFFIDAVIT_ACCEPTANCE",
                    def: "AFFIDAVIT_ACCEPTANCE",
                  })}
                </Checkbox>
              </Form.Item>
            ) : (
              <Typography.Text>
                {Text({
                  tid: "AFFIDAVIT_ACCEPTANCE",
                  def: "AFFIDAVIT_ACCEPTANCE",
                })}
              </Typography.Text>
            )}
          </Col>
          <Row className='QuestionsSubSection affidavitQuestionFirst'>
            <Col
              xs={24}
              lg={12}
              xl={8}
            >
              <Form.Item
                name='STUDENT_2026_PT1_Q28'
                label={Text({
                  tid: "FULL_NAME_OF_APPLICANT",
                  def: "Full Name of Applicant",
                })}
                rules={[
                  {
                    required: true,
                    message: Text({ tid: "REQUIRED", def: "Required" }),
                  },
                ]}
              >
                <Input
                  disabled={true}
                  value={form.getFieldValue("STUDENT_2026_PT1_Q1") || ""}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row className='QuestionsSubSection'>
            <Col
              xs={24}
              lg={12}
              xl={8}
            >
              <Form.Item
                name='STUDENT_2026_PT1_Q29'
                label={Text({
                  tid: "PLACE",
                  def: "PLACE",
                })}
                rules={[
                  {
                    required: true,
                    message: Text({ tid: "REQUIRED", def: "Required" }),
                  },
                ]}
              >
                <Input
                  disabled={disabled || isReviewMode}
                  placeholder={Text({
                    tid: "PLACE_PLACEHOLDER",
                    def: "Ex. Udaipur",
                  })}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row className='QuestionsSubSection'>
            <Col
              xs={24}
              lg={12}
              xl={8}
            >
              <Form.Item
                name='STUDENT_PT1_Q30'
                label={Text({
                  tid: "DATE",
                  def: "DATE",
                })}
                rules={[
                  {
                    required: true,
                    message: Text({ tid: "REQUIRED", def: "Required" }),
                  },
                ]}
              >
                <DatePicker
                  format='DD-MM-YYYY'
                  placeholder={Text({
                    tid: "DATE_PLACEHOLDER",
                    def: "Ex. 21-05-2026",
                  })}
                  disabledDate={(current) =>
                    current && current > dayjs().endOf("day")
                  }
                  disabled={disabled || isReviewMode}
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
          </Row>
        </div>
        <div className='affidavitSection'>
          <Row className=' QuestionsSubSection'>
            <Col
              xs={24}
              lg={12}
              xl={8}
            >
              <DocumentUploadField
                fieldName='STUDENT_2026_PT1_Q31'
                label={Text({
                  tid: "UPLOAD_AFFIDAVIT",
                  def: "Upload affidavit",
                })}
                formId={formId || ""}
                userId={user?.id || ""}
                currentStage={eFORMSTATUS.STAGE1INPROGRESS}
                disabled={disabled || isReviewMode}
                existingDocuments={documentsData}
                onUploadComplete={(docId) =>
                  handleFieldChange(
                    "STUDENT_2026_PT1_Q31",
                    docId,
                    eAnswerType.FILE,
                  )
                }
              />
            </Col>
          </Row>
        </div>
        <Row
          style={
            disabled === true
              ? { marginTop: "var(--bs-item-negative-margin-30)" }
              : undefined
          }
        >
          {disabled === false && !isReviewMode && (
            <Button
              block
              type='primary'
              onClick={handleNextClick}
              disabled={isLoading || !affidavitAccepted}
              className='formSubmitButton'
            >
              {Text({ tid: "NEXT", def: "Next" })}
            </Button>
          )}
          {disabled === false && isReviewMode && (
            <div style={{ display: "flex", gap: "16px", width: "100%" }}>
              <Button
                block
                onClick={handleBackClick}
                disabled={isLoading || isSubmitting}
                className='formSubmitButton'
              >
                {Text({ tid: "BACK", def: "Back" })}
              </Button>
              <Button
                block
                type='primary'
                htmlType='submit'
                disabled={isLoading || !affidavitAccepted}
                className='formSubmitButton'
                loading={isSubmitting}
              >
                {Text({ tid: "SUBMIT", def: "Submit" })}
              </Button>
            </div>
          )}
        </Row>
      </Form>
    </div>
  );
};

export default StudentPartOneCurrent;
